import React, { useEffect, useState, useMemo } from 'react';
import { GET_MY_CHILD_USERS, POST_USER_TRANSFER } from './../../Utils/appConstants';
import { getRequest, postRequest } from './../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import { Select } from 'antd';

/**
 * Role hierarchy map — lower order = more senior.
 * Used to enforce that a parent (To User) is STRICTLY senior to the child (From User).
 */
const ROLE_ORDER = {
    'SA': 1,   // Super Admin
    'A':  2,   // Admin
    'SH': 3,   // State Head
    'SD': 4,   // Super Distributor
    'MD': 5,   // Master Distributor
    'D':  6,   // Distributor
    'P':  7,   // Partner
    'V':  8,   // Vender
    'S':  8,   // Staff
    'M':  9    // Member
};

function TransferUser(props) {
    const history = useHistory();

    const [fromIds, setFromIds]     = useState(null);
    const [toUserId, setToUserId]   = useState(null);
    const [myUsers, setMyUsers]     = useState([]); // enriched with rolecode

    // -------------------- Fetch my children --------------------
    const getMyUser = () => {
        getRequest(GET_MY_CHILD_USERS + "?isAdmin=true", history).then((response) => {
            if (response.success === true) {
                const users = response.data.map((item) => ({
                    value:    item._id,
                    label:    `${item.username} - ${item.name} - ${item.rolecode}`,
                    rolecode: item.rolecode,
                    username: item.username,
                    name:     item.name
                }));
                setMyUsers(users);
            }
        }).catch((error) => {
            console.log(error);
        });
    };

    useEffect(() => {
        getMyUser();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // -------------------- Derived: currently selected From user --------------------
    const selectedFromUser = useMemo(
        () => myUsers.find(u => u.value === fromIds) || null,
        [fromIds, myUsers]
    );

    // -------------------- Filter To-User options by hierarchy --------------------
    // Only users whose role is STRICTLY more senior than the From user's role.
    // Also excludes the From user itself.
    const toUserOptions = useMemo(() => {
        if (!selectedFromUser) return [];

        const fromOrder = ROLE_ORDER[String(selectedFromUser.rolecode || '').toUpperCase()];
        if (!fromOrder) {
            // Unknown role code — fall back to "everyone except self" rather than blocking entirely
            return myUsers.filter(u => u.value !== fromIds);
        }

        return myUsers.filter((u) => {
            if (u.value === fromIds) return false; // no self-transfer
            const uOrder = ROLE_ORDER[String(u.rolecode || '').toUpperCase()];
            return uOrder && uOrder < fromOrder;   // STRICTLY senior
        });
    }, [selectedFromUser, fromIds, myUsers]);

    // -------------------- Handlers --------------------
    const onChangeFromValueSelect = (value) => {
        setFromIds(value);

        // If the previously selected To user is no longer valid, clear it
        const fromUser = myUsers.find(u => u.value === value);
        const toUser   = myUsers.find(u => u.value === toUserId);

        if (!fromUser || !toUser) {
            setToUserId(null);
            return;
        }

        const fromOrder = ROLE_ORDER[String(fromUser.rolecode || '').toUpperCase()];
        const toOrder   = ROLE_ORDER[String(toUser.rolecode   || '').toUpperCase()];

        const invalid =
            value === toUserId ||
            !fromOrder ||
            !toOrder ||
            toOrder >= fromOrder;

        if (invalid) setToUserId(null);
    };

    const onChangeToValueSelect = (value) => {
        setToUserId(value);
    };

    const handleSubmit = () => {
        if (!fromIds)  return toast.error("Please select a From User");
        if (!toUserId) return toast.error("Please select a To User");
        if (fromIds === toUserId) return toast.error("From and To user cannot be the same");

        // Client-side hierarchy re-check (defensive)
        const fromUser = myUsers.find(u => u.value === fromIds);
        const toUser   = myUsers.find(u => u.value === toUserId);
        if (fromUser && toUser) {
            const fromOrder = ROLE_ORDER[String(fromUser.rolecode || '').toUpperCase()];
            const toOrder   = ROLE_ORDER[String(toUser.rolecode   || '').toUpperCase()];
            if (fromOrder && toOrder && toOrder >= fromOrder) {
                return toast.error(
                    `Cannot assign a ${toUser.rolecode} user as parent of a ${fromUser.rolecode} user. ` +
                    `Parent role must be strictly more senior.`
                );
            }
        }

        const payload = { fromIds, toUserId };
        postRequest(POST_USER_TRANSFER, payload, history).then((response) => {
            if (response.success === true) {
                toast.success(response.message);
                window.location.reload();
            } else {
                toast.error(response.message);
            }
        }).catch(() => {});
    };

    // -------------------- Render --------------------
    return (
        <div>
            <div className="content-wrapper pt-2">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-0 align-items-center">
                            <div className="col-sm-6">
                                <h1 className='page-title text-blue font-merriweather'>
                                    Transfer <span className='text-red'>User</span>
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="content">
                    <div className="container-fluid pt-2 pb-5">
                        <div className="row">
                            <div className="col-md-6">
                                <div className='card rounded-0'>
                                    <div className='card-body'>
                                        <div className='row'>
                                            {/* ---- From User ---- */}
                                            <div className='col-lg-12'>
                                                <div className="form-group">
                                                    <label className='date-search-label text-blue'>From User</label>
                                                    <Select
                                                        options={myUsers}
                                                        className="normal-light w-100"
                                                        showSearch
                                                        allowClear
                                                        optionFilterProp="label"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        classNamePrefix="select"
                                                        placeholder="Select"
                                                        value={fromIds || undefined}
                                                        onChange={onChangeFromValueSelect}
                                                    />
                                                </div>
                                            </div>

                                            {/* ---- To User (filtered by hierarchy) ---- */}
                                            <div className='col-lg-12'>
                                                <div className="form-group">
                                                    <label className='date-search-label text-blue'>
                                                        To User
                                                        {selectedFromUser && (
                                                            <small className="text-muted ml-2">
                                                                (only strictly senior roles can be parent)
                                                            </small>
                                                        )}
                                                    </label>
                                                    <Select
                                                        options={toUserOptions}
                                                        className="normal-light w-100"
                                                        showSearch
                                                        allowClear
                                                        disabled={!fromIds}
                                                        optionFilterProp="label"
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        classNamePrefix="select"
                                                        placeholder={fromIds ? "Select" : "Select From User first"}
                                                        value={toUserId || undefined}
                                                        onChange={onChangeToValueSelect}
                                                        notFoundContent={
                                                            fromIds
                                                                ? "No eligible parent found for this role"
                                                                : "Select From User first"
                                                        }
                                                    />
                                                    {fromIds && toUserOptions.length === 0 && (
                                                        <small className="text-danger">
                                                            <i className="fa fa-exclamation-circle mr-1"></i>
                                                            This user's role has no senior parent available.
                                                        </small>
                                                    )}
                                                </div>
                                            </div>

                                            {/* ---- Submit ---- */}
                                            <div className='col-lg-12'>
                                                <div className="form-group text-lg-right">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary rounded-pill pl-4 pr-4 pointer"
                                                        onClick={handleSubmit}
                                                    >
                                                        Submit
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <ToastContainer
                    position="top-center"
                    autoClose={10000}
                    limit={3}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                />
            </div>
        </div>
    );
}

export default TransferUser;